import { Injectable, computed, inject, signal } from '@angular/core'
import { PreOrderDetails, StartOrderDetails, WirelessCreditDecisionResponse, WirelessPDPlusChoice, WirelessPDPlusResponse } from '../../../models/OrderDetails'
import { VIAccountResponse } from '../../../models/sigfan.model'
import { OrderStateServiceV2 } from './orderv2/order-state.service'
import { OrderApiResponseStateService } from './orderv2/order-api-response/order-api-response-state.service'
import { AdvanceBillPaymentStateService } from './orderv2/workflows/advance-bill-payment/advance-bill-payment.state.service'
import { LineConfigurationState } from '../../../models/line-configurations.model'
import { CreditCheckResponse } from '../../../models/order/api-models/order-cc-response.model'
import { Flow, FlowTypes, OrderFlowType } from '../../../models/order/order.model'
// import { mockData } from '../../../../../assets/test-data/mock-wireless-pd'

export const ApiCacheTypes = {
  PREORDER: 'PREORDER',
  STARTORDER: 'STARTORDER',
  PRESQ: 'PRESQ',
  SQ: 'SQ',
  UPDATE_CUSTOMER: 'UPDATE_CUSTOMER',
  INTERNET_PD: 'INTERNET_PD',
  VIDEO_PD: 'VIDEO_PD',
  VOICE_PD: 'VOICE_PD',
  WIRELESS_PD: 'WIRELESS_PD',
  WIRELESS_PDPLUS: 'WIRELESS_PDPLUS',
  INTERNET_UO: 'INTERNET_UO',
  VIDEO_UO: 'VIDEO_UO',
  CC: 'CC',
  CCPLUS: 'CCPLUS',
  CO: 'CO',
  SO: 'SO',
  OQ: 'OQ',
  ATT: 'ATT',
  RC1: 'RC1',
  VI_ACCOUNT: 'VI_AUTH_GET_ACCOUNT',
  ORDER_DATA: 'ORDER_DATA',
  VI_PAYMENT: 'VI_PAYMENT'
} as const;

// Usage example:
// OrderTypes.PREORDER

@Injectable({
  providedIn: 'root'
})
export class OrderStateServiceWrapper {
  orderStateServiceV2 = inject(OrderStateServiceV2)
  orderApiResponseStateService = inject(OrderApiResponseStateService)
  #advanceBillPaymentStateService = inject(AdvanceBillPaymentStateService)

  constructor() { }

  setPreOrder(preOrder: PreOrderDetails) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.PREORDER, preOrder)
  }

  setStartOrder(startOrder: StartOrderDetails) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.STARTORDER, startOrder)
  }

  setAvailableServices(availableServices: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.SQ, availableServices)
  }

  setUpdateCustomer(updateCustomer: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.UPDATE_CUSTOMER, updateCustomer)
  }

  setInternetProductDetails(internetProductDetails: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.INTERNET_PD, internetProductDetails)
  }

  setVideoProductDetails(videoProductDetails: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.VIDEO_PD, videoProductDetails)
  }

  setVoiceProductDetails(voiceProductDetails: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.VOICE_PD, voiceProductDetails)
  }

  setWirelessProductDetails(wirelessProductDetails: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.WIRELESS_PD, wirelessProductDetails)
  }


  // This is a separate method because of it's dynamic nature of lines
  setWirelessProductDetailsPlusFromApi(wirelessProductDetails: any, lineNumber: number) {
    const existingWirelessProductDetailsPlus = this.getWirelessProductDetailsPlus()
    const wirelessProductDetailsWithLineNumber = {
      ...existingWirelessProductDetailsPlus,
      ['PDPLUS_' + lineNumber]: wirelessProductDetails
    }
    
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.WIRELESS_PDPLUS, wirelessProductDetailsWithLineNumber)
  }

  setWirelessProductDetailsPlus(wirelessProductDetails: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.WIRELESS_PDPLUS, wirelessProductDetails)
  }

  setCreditCheck(creditCheckDetails: CreditCheckResponse | null) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.CC, creditCheckDetails)
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.CCPLUS, null)
  }

  setCreditCheckPlus(creditCheckPlusDetails: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.CCPLUS, creditCheckPlusDetails)
  }

  setUpdateOffers(updateOffers: any) {
    this.orderApiResponseStateService.storeUpdateOffer(updateOffers)
  }

  setInternetUpdateOffers(updateOffers: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.INTERNET_UO, updateOffers)
  }

  setVideoUpdateOffers(updateOffers: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.VIDEO_UO, updateOffers)
  }

  setOrderQualification(orderQualificationDetails: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.OQ, orderQualificationDetails)
  }

  setCreateOrder(createOrderDetails: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.CO, createOrderDetails)
  }

  setSubmitOrder(submitOrderDetails: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.SO, submitOrderDetails)
  }

  setPaymentResponse(paymentResponseDetails: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.ATT, paymentResponseDetails)
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.RC1, paymentResponseDetails)
  }

  setViPaymentResponse(viPaymentResponseDetails: any) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.VI_PAYMENT, viPaymentResponseDetails)
  }

  setViAccount(viAccountDetails: VIAccountResponse) {
    this.orderApiResponseStateService.updateCachedData(ApiCacheTypes.VI_ACCOUNT, viAccountDetails)
  }

  getPreOrder = computed(() => {
    return this.orderApiResponseStateService.preOrderData()
  })

  getOrderNumber(): string {
    return this.orderStateServiceV2.orderNumber();
  }

  getStartOrder = computed(() => {
    return this.orderApiResponseStateService.startOrderData()
  })

  getOrder = computed(() => {
    return this.orderStateServiceV2.order();
  })

  getAvailableServices = computed(() => {
    return this.orderApiResponseStateService.serviceAvailabilityData()
  })

  getInternetUpdateOfferData = computed(() => {
    return this.orderApiResponseStateService.internetUpdateOfferData()
  })

  getVideoUpdateOfferData = computed(() => {
    return this.orderApiResponseStateService.videoUpdateOfferData()
  })

  getOrderCustomer = computed(() => {
    return this.orderApiResponseStateService.customer()
  })

  getInternetProductDetails = computed(() => {
    return this.orderApiResponseStateService.internetProductDetailsData()
  })

  getVideoProductDetails = computed(() => {
    return this.orderApiResponseStateService.videoProductDetailsData()
  })

  getWirelessProductDetails = computed(() => {
    // return mockData
    return this.orderApiResponseStateService.wirelessProductDetailsData()
  })

  getWirelessProductDetailsPlus = computed(() => {
    return this.orderApiResponseStateService.wirelessProductDetailsPlusData()
  })

  getCreditCheckDetails = computed(() => {
    return this.orderApiResponseStateService.creditCheckData()
  })

  getOrderQualificationDetails = computed(() => {
    return this.orderApiResponseStateService.orderQualificationData()
  })

  getCreateOrderDetails = computed(() => {
    return this.orderApiResponseStateService.createOrderData()
  })

  getSubmitOrderDetails = computed(() => {
    return this.orderApiResponseStateService.submitOrderData()
  })

  getPaymentProcessDetails = computed(() => {
    return this.orderApiResponseStateService.attData() ?? this.orderApiResponseStateService.rc1Data();
  })

  getVIAccountDetails = computed(() => {
    return this.orderApiResponseStateService.viAuthGetAccountData()
  })

  getVIWirelessAccountDetails = computed(() => {
    return this.orderApiResponseStateService.viAuthGetWirelessAccountData()
  })

  advancedBillPaymentRequired = computed(() => {
    const resp = this.getCreditCheckDetails()
    return resp?.CreditCustomizations &&
      resp.CreditCustomizations.Customization &&
      resp.CreditCustomizations.Customization.Choices &&
      resp.CreditCustomizations.Customization.Choices.Choice &&
      resp.CreditCustomizations.Customization.Choices.Choice.Name === 'Internet Advanced Bill Payment'
  })

  creditResultsRequired = computed(() => {
    const resp = this.getCreditCheckDetails()
    return resp?.CreditCustomizations &&
      resp.CreditCustomizations.Customization &&
      resp.CreditCustomizations.Customization.Choices &&
      resp.CreditCustomizations.Customization.Choices.Choice &&
      resp.CreditCustomizations.Customization.Choices.Choice.Name === 'Credit Results Fee DIRECTV'
  })

  advancedBillPaymentAccepted = computed(() => {
    return this.orderStateServiceV2.order()?.advanceBillPayment.advancedBillPayment;
  })

  todaysChargesCategoryTotal = signal(undefined)

  resetProductSelection() {
    this.setInternetProductDetails(null)
    this.setVideoProductDetails(null)
    this.setWirelessProductDetails(null)
    this.setWirelessProductDetailsPlus(null)
    this.#advanceBillPaymentStateService.clear();
  }

  retrieveWirelessResponse(resp: any) {
    return resp.ResponseHeader?.ProductResponse?.find(
      (response: any) => response.Type.toLowerCase() === 'wireless'
    )
  }

  reset() {
    this.setPreOrder(null)
    this.setStartOrder(null)
    this.setAvailableServices(null)
    this.setInternetProductDetails(null)
    this.setVideoProductDetails(null)
    this.setWirelessProductDetails(null)
    this.setWirelessProductDetailsPlus(null)
    this.setCreditCheck(null)
    this.setOrderQualification(null)
    this.setInternetUpdateOffers(null)
    this.setVideoUpdateOffers(null)
    this.setCreateOrder(null)
    this.setSubmitOrder(null)
    this.setPaymentResponse(null)
    this.setViAccount(null)
    this.orderStateServiceV2.clearAll();
    this.orderApiResponseStateService.clear();
    this.setViPaymentResponse(null)
  }

  /**
   * Returns true if the given step is accessible (all previous steps are completed)
   */
  isRouteAccessible(route: string): boolean {
    const currentStep = this.getCurrentRoute();
    if (currentStep && currentStep !== route) {
      return false; // Current step is beyond the requested step
    }
    return true;
  }

  getCurrentRoute(): string {
    return this.orderStateServiceV2.order()?.currentRoute;
  }

  setCurrentRoute(route: string) {
    this.orderStateServiceV2.updateOrder({ currentRoute: route });
  }

  getWirelessPdplusLineData(lineNumber: number) {
    const wirelessProductDetailsPlus = this.getWirelessProductDetailsPlus()
    if (!wirelessProductDetailsPlus) {
      return null;
    }
    return wirelessProductDetailsPlus['PDPLUS_' + lineNumber];
  }

  findChoiceInPdplusBySubGroupNameOrChoiceCode(lineData: WirelessPDPlusResponse, subGroupName?: string, choiceCode?: string): WirelessPDPlusChoice | null {
    if (!lineData?.Components?.Component?.Customizations?.Customization) {
      return null;
    }

    for (const customization of lineData.Components.Component.Customizations.Customization) {
      if (customization.Choices?.Choice) {
        const choice = customization.Choices.Choice.find(choice => {
          const conditions = [];

          if (subGroupName != null) {
            conditions.push(choice.ChoiceSubGroupName === subGroupName);
          }

          if (choiceCode != null) {
            conditions.push(choice.ChoiceCode === choiceCode);
          }

          return conditions.length > 0 && conditions.some(condition => condition);
        });

        if (choice) {
          return choice;
        }
      }
    }
    return null;
  }

  getCreditDecisionOnType(type: OrderFlowType | Flow): any | WirelessCreditDecisionResponse | null {
    const creditCheckDetails = this.getCreditCheckDetails();
    switch (type) {
      // Voice requires an active Internet plan, so it shares the Internet credit decision.
      // There is no dedicated VoiceCreditDecision in the CC response.
      case FlowTypes.VOICE:
      case FlowTypes.INTERNET:
        if (creditCheckDetails?.OOFCreditDecision !== null) {
          return creditCheckDetails?.OOFCreditDecision;
        }
        return creditCheckDetails?.ATTCreditDecision === null ? creditCheckDetails?.RC1CreditDecision : creditCheckDetails?.ATTCreditDecision;
      case FlowTypes.VIDEO:
        return creditCheckDetails?.DTVCreditDecision;
      case FlowTypes.WIRELESS:
        return creditCheckDetails?.WirelessCreditDecision;
    }
    return null;
  }

  getLineConfigurationState(): LineConfigurationState {
    return this.orderStateServiceV2.order()?.wirelessFlow?.lineConfigurations;
  }

  // To get all line numbers that have configurations
  getConfiguredLineNumbers(): number[] {
    const state = this.getLineConfigurationState();
    if (!state) return [];
    return Object.keys(state)
      .map(key => parseInt(key.replace('line', '')))
      .filter(num => !isNaN(num))
      .sort((a, b) => a - b);
  }


}
